import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { toast } from '../components/ui/use-toast';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

/**
 * ClaimVerify page - handles the email verification callback for business claims.
 * After user clicks the verification link, this page:
 * 1. Verifies the email
 * 2. Signs in the user
 * 3. Updates the trader record to link it to the new user
 * 4. Marks the business as claimed (mockup=false)
 */
const ClaimVerify: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('Verifying your email...');

  useEffect(() => {
    const completeClaimProcess = async () => {
      try {
        // Get trader ID from URL or localStorage
        const traderId = searchParams.get('traderId') || localStorage.getItem('claim_trader_id');
        const storedEmail = localStorage.getItem('claim_email');
        const storedPassword = localStorage.getItem('temp_password');

        console.log('Claim verification started:', { traderId, storedEmail });

        if (!traderId) {
          throw new Error('No business ID found. Please try the claim process again.');
        }

        // Check if user is already signed in from the verification link
        const { data: { session } } = await supabase.auth.getSession();
        
        let userId: string | null = null;

        if (session?.user) {
          // User is signed in from verification link
          userId = session.user.id;
          console.log('User already signed in:', userId);
        } else if (storedEmail && storedPassword) {
          // Try to sign in with stored credentials
          console.log('Attempting to sign in with stored credentials');
          const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
            email: storedEmail,
            password: storedPassword
          });

          if (signInError) {
            console.error('Sign in error:', signInError);
            throw new Error('Failed to sign in. Please try logging in manually.');
          }

          userId = signInData.user?.id || null;
        }

        if (!userId) {
          throw new Error('Could not verify your account. Please try logging in manually.');
        }

        setMessage('Linking business to your account...');

        // Update the trader record to link it to the user and mark as claimed
        // Also replace any internal placeholder email with the real owner email used during claim
        const { error: updateError } = await supabase
          .from('traders')
          .update({
            user_id: userId,
            mockup: false, // Mark as claimed (no longer mockup)
            verified: true, // Mark as verified since email was confirmed
            email: storedEmail || undefined,
            updated_at: new Date().toISOString()
          })
          .eq('id', parseInt(traderId));

        if (updateError) {
          console.error('Error updating trader:', updateError);
          throw new Error('Failed to link business to your account. Please contact support.');
        }

        // Clear stored claim data
        localStorage.removeItem('claim_trader_id');
        localStorage.removeItem('claim_email');
        localStorage.removeItem('temp_password');

        setStatus('success');
        setMessage('Business claimed successfully!');

        toast({
          title: 'Success!',
          description: 'You have successfully claimed your business. Redirecting to dashboard...',
        });

        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);

      } catch (err) {
        console.error('Claim verification error:', err);
        setStatus('error');
        setMessage(err instanceof Error ? err.message : 'Verification failed');
        
        toast({
          title: 'Verification Failed',
          description: err instanceof Error ? err.message : 'Failed to complete claim process',
          variant: 'destructive',
        });
      }
    };

    completeClaimProcess();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying...</h1>
              <p className="text-gray-600">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Success!</h1>
              <p className="text-gray-600 mb-4">{message}</p>
              <p className="text-sm text-gray-500">Redirecting to your dashboard...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h1>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Go to Login
                </button>
                <button
                  onClick={() => navigate('/trades')}
                  className="w-full px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Browse Traders
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClaimVerify;
